import { useMutation, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import axios from 'axios'
import {
  Button,
  ButtonColor,
  Loader,
  NotificationType,
  PortfolioGrid,
  VideoUpload,
} from '../../../components'
import {
  UPDATE_CREATOR,
  GetPortfolioByCreatorResponse,
  GET_PORTFOLIO_BY_CREATOR,
  GET_PORTFOLIO_ITEM_UPLOAD_URL,
  CREATE_PORTFOLIO_ITEM,
} from '../../../gql'
import {
  Creator,
  CreatorOnboardingStep,
  GetPortfolioSort,
  GetUploadUrlResponse,
  MutationCreatePortfolioItemArgs,
  MutationGetPortfolioItemUploadUrlArgs,
  QueryGetPortfolioByCreatorArgs,
} from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import { useNotification } from '../../../utils/hooks'
import { Steps } from '../components/Steps'
import { Bullets } from '../components/Bullets'

interface Props {
  creator: Creator
}

const SetPortfolio = ({ creator }: Props) => {
  const [updateCreator, { loading: updateCreatorLoading }] =
    useMutation(UPDATE_CREATOR)
  const { addNotification } = useNotification()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [mime, setMime] = useState('')
  const [uploadProgress, setUploadProgress] = useState(0)
  // TODO: Video shows black screen when navigating back (component remounts).
  //   Root cause: browser makes new range requests to CDN instead of disk cache.
  //   See SetIntroVideo for full investigation notes and solutions to try.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // Revoke object URL on unmount or when previewUrl changes to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const [getPortfolioItemUploadUrl] = useMutation<
    { getPortfolioItemUploadUrl: GetUploadUrlResponse },
    MutationGetPortfolioItemUploadUrlArgs
  >(GET_PORTFOLIO_ITEM_UPLOAD_URL)

  const [createPortfolioItem] = useMutation<
    any,
    MutationCreatePortfolioItemArgs
  >(CREATE_PORTFOLIO_ITEM, {
    refetchQueries: [
      {
        query: GET_PORTFOLIO_BY_CREATOR,
        variables: {
          creatorId: creator.id,
          options: {
            skip: 0,
            limit: 12,
            sort: GetPortfolioSort.newest,
          },
        },
      },
    ],
  })

  const reset = () => {
    setSelectedFile(null)
    setMime('')
    setUploadProgress(0)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  const {
    loading: getPortfolioLoading,
    error: getPortfolioError,
    data: getPortfolioData,
  } = useQuery<GetPortfolioByCreatorResponse, QueryGetPortfolioByCreatorArgs>(
    GET_PORTFOLIO_BY_CREATOR,
    {
      variables: {
        creatorId: creator.id,
        options: {
          skip: 0,
          limit: 12,
          sort: GetPortfolioSort.newest,
        },
      },
    },
  )

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewCreatorsOnboardingSetPortfolio)
  }, [])

  const goBack = async () => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            onboarding: CreatorOnboardingStep.setIntroVideo,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  const onUpdateCreator = async (event: React.MouseEvent) => {
    event.preventDefault()

    setError(null)

    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            onboarding: CreatorOnboardingStep.setShippingAddress,
          },
        },
      })
    } catch (error) {
      console.log('@error updateCreator', error.message)
    }

    setLoading(false)
  }

  const uploadContent = async (
    selectedFile: File,
    mime: string,
    contentDimensions: { w: number | null; h: number | null },
  ) => {
    setError(null)
    setLoading(true)

    try {
      //make sure to call GET_CONTENT_UPLOAD_URL every time when uploading a new file
      const { data } = await getPortfolioItemUploadUrl({
        variables: {
          creatorId: creator.id,
          mime,
        },
      })

      // push content to S3
      await axios.put(data.getPortfolioItemUploadUrl.uploadUrl, selectedFile, {
        headers: {
          'Content-Type': mime,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
          setUploadProgress(percentCompleted)
        },
      })

      await createPortfolioItem({
        variables: {
          creatorId: creator.id,
          input: {
            videoFileName: data.getPortfolioItemUploadUrl.fileName,
            videoWidth: contentDimensions.w,
            videoHeight: contentDimensions.h,
          },
        },
      })

      addNotification(`Portfolio video uploaded`, NotificationType.success)
      reset()
    } catch (error) {
      console.log('Error Creating portfolio video', error?.message)

      addNotification(
        `Something went wrong. Please try again.`,
        NotificationType.error,
      )
    }

    setLoading(false)
  }

  const handleFileInput = (
    file: File,
    size?: {
      w: number | null
      h: number | null
    },
  ) => {
    if (!file) {
      setSelectedFile(null)
      setMime('')
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }
      return
    }

    setSelectedFile(file)
    setMime(file.type)
    setError(null)
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)

    // Direct call with explicit params instead of useEffect -- values come from function args,
    // not from state that may not be updated yet (React batching is not guaranteed)
    uploadContent(file, file.type, size)
  }

  // if (loading) {
  //   return <Loader />
  // }

  if (error || getPortfolioError) {
    console.log('getPortfolioError', getPortfolioError?.message)
    console.log('error', error)
  }

  return (
    <div className='min-h-screen flex flex-col'>
      <div className='flex-grow'>
        <Steps
          creator={creator}
          stepCurrent={CreatorOnboardingStep.setPortfolio}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <div className='md:shadow rounded-t-md sm:overflow-hidden mb-8'>
              <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                <div className='sm:mx-auto'>
                  <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
                    Portfolio
                  </h2>
                  <div className='mt-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-md'>
                    <p className='text-sm text-blue-900 font-semibold'>
                      💡 These videos will be displayed to brands and are the
                      most important part of your application. Only 7% of
                      creators who apply to Clip are accepted.
                    </p>
                  </div>
                  <p className='mt-4 text-sm text-gray-600'>
                    Upload at least {creator.minPortfolioItems} of your{' '}
                    <strong>absolute best</strong> UGC videos from previous
                    brand collaborations. Your portfolio should showcase
                    different styles of UGC (unboxing, testimonial, how-to).
                    Videos should be between 15 seconds to 1 minute.
                  </p>
                  <div className='mt-4 p-3 bg-gray-50 border border-gray-300 rounded-md'>
                    <p className='text-sm text-gray-700 font-medium mb-2'>
                      Required:
                    </p>
                    <ul className='text-sm text-gray-700 list-disc list-inside space-y-1'>
                      <li>Videos must be in English</li>
                      <li>
                        Must include talking to the camera/voiceover (silent
                        videos are not accepted)
                      </li>
                      <li>
                        No AI voiceovers or AI-generated content (must be
                        authentic, human-created content)
                      </li>
                    </ul>
                  </div>
                  <div className='mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-md'>
                    <p className='text-sm text-red-900 font-semibold mb-2'>
                      ⚠️ WARNING: Your application will be rejected if you:
                    </p>
                    <ul className='text-sm text-red-800 list-disc list-inside space-y-1'>
                      <li>
                        Submit videos that aren't UGC (User-Generated Content)
                        you created for brands.
                      </li>
                      <li>
                        Upload any other content types (vlogs, lifestyle
                        content, etc.).
                      </li>
                    </ul>
                  </div>
                  <p className='mt-4 text-sm text-gray-600'>
                    <a
                      href='https://useclip.com/ugc-content'
                      className='text-blue-600 font-medium hover:text-blue-700'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      See examples
                    </a>{' '}
                    of approved videos by other creators on Clip.
                  </p>
                </div>

                <div className='mt-6 w-full'>
                  {loading && previewUrl && (
                    <div className='flex flex-col justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6'>
                      <div className='mb-4 flex mx-auto'>
                        <video
                          className='max-h-[350px]'
                          src={previewUrl}
                          controls
                        />
                      </div>
                    </div>
                  )}
                  {!loading && <VideoUpload onChange={handleFileInput} />}
                  {error && (
                    <p className='text-red-500 text-sm mt-2'>{error}</p>
                  )}
                </div>

                {getPortfolioLoading && <Loader />}
                {!getPortfolioLoading && getPortfolioData && (
                  <div className='-mx-4'>
                    <PortfolioGrid
                      items={getPortfolioData.getPortfolioByCreator.items}
                      isFullScreen={true}
                      isDeleteAvailable={true}
                      minPortfolioItems={creator.minPortfolioItems}
                      canDeletePortfolioItems={creator.canDeletePortfolioItems}
                    />
                  </div>
                )}
              </div>

              <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
                <Button
                  title='Go back'
                  type='button'
                  colour={ButtonColor.white}
                  loading={loading || updateCreatorLoading}
                  disabled={loading || updateCreatorLoading}
                  onClick={goBack}
                />
                <Button
                  title={
                    loading && uploadProgress > 0
                      ? `Uploading... ${uploadProgress}%`
                      : 'Continue'
                  }
                  type='button'
                  loading={
                    !(loading && uploadProgress > 0)
                      ? loading || updateCreatorLoading
                      : undefined
                  }
                  disabled={loading || updateCreatorLoading}
                  loadingPercentage={loading ? uploadProgress : undefined}
                  onClick={(event) => {
                    if (
                      getPortfolioData?.getPortfolioByCreator?.items?.length <
                      creator.minPortfolioItems
                    ) {
                      setError(
                        `Please upload at least ${creator.minPortfolioItems} videos.`,
                      )
                      return
                    }
                    onUpdateCreator(event)
                  }}
                  width='160px'
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={CreatorOnboardingStep.setPortfolio} />
    </div>
  )
}

export { SetPortfolio }
